import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { BusinessCard } from '../components/BusinessCard';
import { CategoryFilter } from '../components/CategoryFilter';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, SlidersHorizontal, Store, X, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const COUNTRIES = [
  { code: "US", name: "United States", region: "North America" },
  { code: "CA", name: "Canada", region: "North America" },
  { code: "GB", name: "United Kingdom", region: "Europe" },
  { code: "JM", name: "Jamaica", region: "Caribbean" },
  { code: "TT", name: "Trinidad and Tobago", region: "Caribbean" },
  { code: "BB", name: "Barbados", region: "Caribbean" },
  { code: "BS", name: "Bahamas", region: "Caribbean" },
  { code: "NG", name: "Nigeria", region: "Africa" },
  { code: "GH", name: "Ghana", region: "Africa" },
  { code: "KE", name: "Kenya", region: "Africa" },
  { code: "ZA", name: "South Africa", region: "Africa" },
  { code: "BR", name: "Brazil", region: "South America" },
  { code: "FR", name: "France", region: "Europe" },
  { code: "DE", name: "Germany", region: "Europe" },
  { code: "NL", name: "Netherlands", region: "Europe" },
  { code: "AU", name: "Australia", region: "Oceania" },
  { code: "MX", name: "Mexico", region: "North America" },
  { code: "PR", name: "Puerto Rico", region: "Caribbean" },
  { code: "CR", name: "Costa Rica", region: "Central America" },
  { code: "PA", name: "Panama", region: "Central America" },
];

const DirectoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedState, setSelectedState] = useState(searchParams.get('state') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || '');

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedState) params.append('state', selectedState);
      if (selectedCity) params.append('city', selectedCity);
      if (selectedCountry) params.append('country', selectedCountry);

      const response = await axios.get(`${API}/businesses?${params.toString()}`);
      setBusinesses(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch businesses:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
    // Update URL params
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedState) params.set('state', selectedState);
    if (selectedCity) params.set('city', selectedCity);
    if (selectedCountry) params.set('country', selectedCountry);
    setSearchParams(params);
  }, [selectedCategory, selectedState, selectedCity, selectedCountry]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBusinesses();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedState('');
    setSelectedCity('');
    setSelectedCountry('');
  };

  const hasActiveFilters = searchQuery || selectedCategory || selectedState || selectedCity || selectedCountry;

  // Check if US is selected to show state filter
  const showStateFilter = !selectedCountry || selectedCountry === 'United States';

  return (
    <div className="min-h-screen bg-stone-white" data-testid="directory-page">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-8 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-2" data-testid="directory-title">
            Business Directory
          </h1>
          <p className="text-stone-600 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Discover Black-owned businesses across the United States and around the world
          </p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-lg border-b border-stone-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Browse by Category */}
          <div className="mb-4">
            <p className="text-sm font-medium text-stone-600 mb-2">Browse by Category</p>
            <CategoryFilter
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
            />
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                type="text"
                placeholder="Search businesses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full border-stone-200"
                data-testid="search-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full px-6 h-12"
              data-testid="search-btn"
            >
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`rounded-full h-12 px-4 ${showFilters ? 'bg-emerald-deep/5 border-emerald-deep' : ''}`}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="w-5 h-5" />
            </Button>
          </form>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-stone-200 flex flex-wrap gap-4" data-testid="advanced-filters">
              <div className="w-48">
                <Select value={selectedCountry || "all"} onValueChange={(value) => {
                  const newValue = value === "all" ? "" : value;
                  setSelectedCountry(newValue);
                  // Clear state if switching away from US
                  if (newValue && newValue !== 'United States') {
                    setSelectedState('');
                  }
                }}>
                  <SelectTrigger data-testid="country-select">
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {showStateFilter && (
                <div className="w-48">
                  <Select value={selectedState || "all"} onValueChange={(value) => setSelectedState(value === "all" ? "" : value)}>
                    <SelectTrigger data-testid="state-select">
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All States</SelectItem>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="w-48">
                <Input
                  type="text"
                  placeholder="City"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="h-10"
                  data-testid="city-input"
                />
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-stone-500 hover:text-stone-700"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Results Count */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-stone-600" data-testid="results-count">
            {loading ? 'Loading...' : `${businesses.length} businesses found`}
          </p>
        </div>

        {/* Business Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl h-80 animate-pulse" />
            ))}
          </div>
        ) : businesses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="business-grid">
            {businesses.map((business) => (
              <BusinessCard key={business.business_id} business={business} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl" data-testid="no-results">
            <Store className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-display text-xl font-semibold text-stone-700 mb-2">
              No Businesses Found
            </h3>
            <p className="text-stone-500 mb-6">
              {hasActiveFilters
                ? 'Try adjusting your filters or search terms'
                : 'Be the first to list a business in this category'}
            </p>
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="outline"
                className="rounded-full"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default DirectoryPage;
